import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

export type ComponentCloseRequest = {
  requestId: string;
};

type ComponentCloseContextValue = {
  closeRequest: ComponentCloseRequest | null;
  requestClose: () => void;
  closeImmediately: () => void;
  markCloseRequestHandled: (requestId: string) => void;
};

const ComponentCloseContext = createContext<ComponentCloseContextValue | null>(
  null,
);

function createRequestId() {
  if (typeof uuidv4 !== "undefined" && typeof uuidv4 === "function") {
    return uuidv4();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ComponentCloseProvider({
  closeImmediately,
  onRequestClose,
  children,
}: {
  closeImmediately: () => void;
  onRequestClose?: () => boolean | Promise<boolean>;
  children:
    | ReactNode
    | ((api: {
        requestClose: () => void;
        closeImmediately: () => void;
      }) => ReactNode);
}) {
  const [closeRequest, setCloseRequest] =
    useState<ComponentCloseRequest | null>(null);

  const handledRequestIdRef = useRef<string | null>(null);

  const markCloseRequestHandled = useCallback((requestId: string) => {
    handledRequestIdRef.current = requestId;
    setCloseRequest((currentRequest) =>
      currentRequest?.requestId === requestId ? null : currentRequest,
    );
  }, []);

  const requestClose = useCallback(() => {
    if (onRequestClose) {
      void Promise.resolve(onRequestClose()).then((canClose) => {
        if (canClose) {
          closeImmediately();
        }
      });
      return;
    }

    const requestId = createRequestId();

    handledRequestIdRef.current = null;

    setCloseRequest({
      requestId,
    });
  }, [closeImmediately, onRequestClose]);

  /**
   * If no child handles the close request, close immediately.
   *
   * This keeps the system agnostic:
   * - RecordScreen can handle the request.
   * - AutoScreen can handle the request.
   * - Other components do nothing and close normally.
   */
  useEffect(() => {
    if (!closeRequest) return;

    const timeoutId = window.setTimeout(() => {
      const closeIfAllowed = async () => {
        if (handledRequestIdRef.current === closeRequest.requestId) {
          return;
        }

        if (handledRequestIdRef.current === closeRequest.requestId) {
          return;
        }

        closeImmediately();
      };

      if (handledRequestIdRef.current === closeRequest.requestId) {
        return;
      }

      void closeIfAllowed();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [closeRequest, closeImmediately]);

  const value = useMemo<ComponentCloseContextValue>(
    () => ({
      closeRequest,
      requestClose,
      closeImmediately,
      markCloseRequestHandled,
    }),
    [closeRequest, requestClose, closeImmediately, markCloseRequestHandled],
  );

  return (
    <ComponentCloseContext.Provider value={value}>
      {typeof children === "function"
        ? children({
            requestClose,
            closeImmediately,
          })
        : children}
    </ComponentCloseContext.Provider>
  );
}

export function useComponentCloseContext() {
  return useContext(ComponentCloseContext);
}

export function useControlledComponentClose(
  onRequestClose: (completeClose: () => void) => void,
  shouldHandleRequest?: () => boolean,
) {
  const closeContext = useComponentCloseContext();
  const handledRequestIdRef = useRef<string | null>(null);
  const onRequestCloseRef = useRef(onRequestClose);
  const shouldHandleRequestRef = useRef(shouldHandleRequest);

  useEffect(() => {
    onRequestCloseRef.current = onRequestClose;
  }, [onRequestClose]);

  useEffect(() => {
    shouldHandleRequestRef.current = shouldHandleRequest;
  }, [shouldHandleRequest]);

  useEffect(() => {
    if (!closeContext?.closeRequest) return;

    const { requestId } = closeContext.closeRequest;

    if (handledRequestIdRef.current === requestId) {
      return;
    }

    if (shouldHandleRequestRef.current && !shouldHandleRequestRef.current()) {
      return;
    }

    handledRequestIdRef.current = requestId;
    closeContext.markCloseRequestHandled(requestId);

    onRequestCloseRef.current(() => {
      closeContext.closeImmediately();
    });
  }, [closeContext?.closeRequest?.requestId]);
}
